import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import LogoPagaliImg from '../../assets/logo-pagali-color@2x.png';
import { pagaliEvents } from '../../utils/pixel';
import { gtagConversions } from '../../components/GtagLoader';

const OTP_LENGTH = 6;

const VerifyOTP: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  const { preRegistoId, email, telefone } = (location.state as {
    preRegistoId?: string;
    email?: string;
    telefone?: string;
  }) || {};

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no registration data
  useEffect(() => {
    if (!preRegistoId) {
      navigate('/register');
    }
  }, [preRegistoId, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only accept digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];

    if (value.length > 1) {
      // Handle paste
      const digits = value.slice(0, OTP_LENGTH - index).split('');
      digits.forEach((digit, i) => {
        if (index + i < OTP_LENGTH) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);

      // Focus last filled input or next empty
      const lastIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
      inputRefs.current[lastIndex]?.focus();
    } else {
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next input
      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    // Auto-submit when complete
    const code = newOtp.join('');
    if (code.length === OTP_LENGTH && !newOtp.includes('')) {
      handleVerify(code);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (code?: string) => {
    const otpCode = code || otp.join('');

    if (otpCode.length !== OTP_LENGTH) {
      showError('Introduza o codigo completo');
      return;
    }

    setIsVerifying(true);

    try {
      // Step 1: Verify OTP
      const verifyResponse = await api.post('/registration/verify', {
        preRegistoId,
        codigo: otpCode
      });

      if (!verifyResponse.data.success) {
        showError(verifyResponse.data.error || 'Codigo invalido');
        setOtp(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        return;
      }

      // Step 2: Complete registration
      const completeResponse = await api.post('/registration/complete', {
        preRegistoId
      });

      if (completeResponse.data.success) {
        const { pending, email: respEmail, nome, tipoConta, userId } =
          completeResponse.data.data || {};

        // Conversão real: a conta existe. O servidor envia o mesmo evento pelo
        // funil de activação — o userId dá-lhes event_id igual, para o Meta
        // deduplicar em vez de contar duas vezes.
        pagaliEvents.registerCompleted(userId);
        gtagConversions.completeRegistration();

        if (pending) {
          // Conta Developer/Empresa — aguarda aprovação, não faz login automático
          navigate('/register/pending', {
            state: { email: respEmail, nome, tipoConta }
          });
        } else {
          showSuccess('Conta criada com sucesso!');
          navigate('/login', {
            state: { message: 'Conta criada! Faca login para continuar.' }
          });
        }
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao verificar codigo';
      showError(message);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isLoading) return;

    setIsLoading(true);

    try {
      const response = await api.post('/registration/resend-otp', {
        preRegistoId
      });

      if (response.data.success) {
        showSuccess('Novo codigo enviado!');
        setResendCountdown(60);
        setCanResend(false);
        setOtp(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao reenviar codigo';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const maskedEmail = email?.replace(/(.{2})(.*)(@.*)/, '$1***$3') || '';
  const maskedPhone = telefone?.replace(/(.{3})(.*)(.{2})/, '$1***$3') || '';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={LogoPagaliImg}
            alt="Pagali"
            className="h-10 mx-auto mb-6"
          />

          {/* Email Icon */}
          <div className="w-16 h-16 bg-pagali-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-pagali-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h1 className="text-2xl font-semibold text-gray-800 mb-2">
            Verificar email
          </h1>
          <p className="text-gray-500">
            Enviamos um codigo de verificacao para{' '}
            <span className="font-medium text-gray-700">{maskedEmail}</span>
            {maskedPhone && (
              <>
                {' '}e{' '}
                <span className="font-medium text-gray-700">{maskedPhone}</span>
              </>
            )}
          </p>
        </div>

        {/* OTP Input */}
        <div className="flex justify-center gap-3 mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isVerifying}
              className={`w-12 h-14 text-center text-2xl font-bold rounded-lg border-2
                ${digit ? 'border-pagali-primary bg-pagali-primary/5' : 'border-gray-300'}
                focus:border-pagali-primary focus:ring-2 focus:ring-pagali-primary/20
                disabled:opacity-50 transition-all`}
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          onClick={() => handleVerify()}
          disabled={isVerifying || otp.join('').length !== OTP_LENGTH}
          className="w-full bg-pagali-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-pagali-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isVerifying ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              A verificar...
            </span>
          ) : (
            'Verificar'
          )}
        </button>

        {/* Resend */}
        <div className="text-center mt-6">
          <p className="text-gray-500 mb-2">
            Nao recebeu o codigo?
          </p>
          {canResend ? (
            <button
              onClick={handleResend}
              disabled={isLoading}
              className="text-pagali-primary font-medium hover:underline disabled:opacity-50"
            >
              {isLoading ? 'A enviar...' : 'Reenviar codigo'}
            </button>
          ) : (
            <p className="text-gray-400">
              Reenviar em {resendCountdown}s
            </p>
          )}
        </div>

        {/* Back Link */}
        <p className="text-center mt-6">
          <button
            onClick={() => navigate('/register')}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            Voltar ao inicio
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyOTP;
